import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  claimComplete,
  claimOpen,
  claimResendOtp,
  claimVerifyOtp,
  type ClaimOpenResult,
  type CommerceSession,
} from '@services/partnerService';
import { FieldV4, TextInputV4 } from '@components/v4/Input/Input';
import { V4 } from '@components/v4/tokens';
import { partnerEvent, partnerTag, partnerUpgrade } from '../analytics';
import { Button, C, Card, ErrorNote, Field, Muted, PageShell, SectionTitle, Title } from '../ui';
import ClaimPlanStep from '../ClaimPlanStep';

// Mismo cooldown que el registro normal (ContactDataStep.tsx: TIMER_SECONDS=60) para reenviar OTP.
const RESEND_SECONDS = 60;

// El OTP del claim es de 4 dígitos (igual que el registro normal). Validamos en vivo al alcanzar
// esta longitud, sin esperar al submit final.
const OTP_LENGTH = 4;

// Campos required cuya falta marcamos con error inline (FieldV4) en lugar de la burbuja nativa.
type FieldKey = 'nombre' | 'correo' | 'usuario' | 'password' | 'terms';

// Enmascara el telefono a sus ultimos 4 digitos (ej. "••••4200"). El canal se muestra SIEMPRE,
// aunque el proveedor de WhatsApp falle (otp_sent=false), para indicar a donde llego el codigo.
const maskPhone = (p?: string | null): string | null => {
  if (!p) return null;
  const digits = p.replace(/\D/g, '');
  if (digits.length < 4) return null;
  return `••••${digits.slice(-4)}`;
};

// Promueve el draft a sesion plena del comercio: escribe localStorage (lo que lee checkAuth del
// panel). NO usa el AuthContext del panel (app separada). Separamos la persistencia del redirect:
// tras el claim persistimos la sesion (para que axiosInstance ya mande el JWT de comercio +
// id_comercio al gateway en el paso de plan) y SOLO redirigimos al elegir plan/gratis.
const persistSession = (s: CommerceSession) => {
  localStorage.setItem('id_commerce', s.id_commerce);
  localStorage.setItem('id_user', s.id_user);
  localStorage.setItem('id_path', s.id_path);
  localStorage.setItem('commerce_name', s.commerce_name);
  localStorage.setItem('commerce_logo', s.commerce_logo || '');
  localStorage.setItem('user_name', s.user_name);
  localStorage.setItem('token', s.token);
  localStorage.setItem('userData', JSON.stringify(s.user));
  // Clear stale keys from a prior session in the SAME browser (the /pre-registro link is public,
  // so localStorage may hold a previous, already-onboarded commerce). The panel router gates the
  // onboarding wizard strictly on `onboardingDate === null` (router.tsx), so a leftover non-null
  // onboarding_finish_date would make the freshly claimed owner SKIP the mandatory wizard. Same
  // staleness applies to user_type: a stale value would mis-scope this new owner's permissions.
  if (s.onboarding_finish_date) localStorage.setItem('onboarding_finish_date', s.onboarding_finish_date);
  else localStorage.removeItem('onboarding_finish_date');
  if (s.user_type) localStorage.setItem('user_type', s.user_type);
  else localStorage.removeItem('user_type');
  localStorage.setItem('is_root', String(Boolean(s.is_root)));
  // Multisucursal: persistir los modulos/permisos del usuario igual que `AuthContext.login()`. Sin
  // esto, al recargar el panel `checkAuth` no encuentra `user_modules` y cae a [] → `hasUserModule`
  // daria false para una sesion no-root (perdida silenciosa de permisos). Solo se escribe si el
  // backend lo envio; si no viene, se limpia cualquier valor stale de una sesion previa en el mismo
  // navegador (el enlace /pre-registro es publico y localStorage puede traer un comercio anterior).
  if (s.user_modules) localStorage.setItem('user_modules', JSON.stringify(s.user_modules));
  else localStorage.removeItem('user_modules');
};

const goToPanel = () => window.location.assign('/');

// Aviso PROMINENTE del estado del envío del OTP al cargar la página (no es un toast efímero):
// tone='info' (azul de marca) = "código enviado por WhatsApp"; tone='warning' (ámbar) = "no se pudo
// enviar, reintenta". El VERDE se reserva exclusivamente para "Código verificado ✓", para que el
// usuario distinga "te llegó" (azul) de "ya quedó validado" (verde) sin ambigüedad.
const Banner = ({ tone, children }: { tone: 'info' | 'warning'; children: ReactNode }) => {
  const info = tone === 'info';
  return (
    <div
      style={{
        margin: '0 0 16px',
        padding: '12px 14px',
        background: info ? V4.secondaryFaded : V4.warningFaded,
        border: `1px solid ${info ? V4.secondary : V4.warning}`,
        borderRadius: 10,
        fontSize: 14,
        lineHeight: 1.45,
        color: info ? V4.secondaryDark : '#8A5A00',
      }}
    >
      {children}
    </div>
  );
};

const ClaimPage = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [data, setData] = useState<ClaimOpenResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [otp, setOtp] = useState('');
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [terms, setTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const [cooldown, setCooldown] = useState(0); // segundos restantes para volver a reenviar
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  // Invalida cualquier verifyOtp en vuelo: si el dueño reenvia el codigo mientras la verificacion
  // del codigo anterior sigue en curso, la respuesta tardia (isOtpValid=true) NO debe pisar el otp
  // recien limpiado por onResend — dejaria "Codigo verificado (check)" con el input vacio y
  // bloqueado, sin forma de recuperarse salvo esperar otro cooldown completo.
  const verifyRequestRef = useRef(0);

  // Tras el claim exitoso pasamos al paso de plan (en vez de redirigir directo). La sesion ya
  // quedo persistida en localStorage, asi que las llamadas a /v1/planes y convertFreemium viajan
  // autenticadas como el comercio.
  const [claimedSession, setClaimedSession] = useState<CommerceSession | null>(null);
  // Reanudar pago (comercio YA reclamado, ver claimLink.js/open.js): el pago ya se resolvió por
  // otra vía (carrera con el webhook) entre que se generó el enlace y que se abrió — nada que
  // hacer aquí, solo informar.
  const [planAlreadyResolved, setPlanAlreadyResolved] = useState(false);

  const open = useCallback(async () => {
    setLoading(true);
    try {
      const res = await claimOpen(token);
      setData(res);

      // Comercio YA reclamado: el token solo pudo emitirse porque quedaba un pago de plan
      // pendiente. Sin formulario de OTP/crear-cuenta — o ya se resolvió (informar), o se retoma
      // directo el paso de plan con la sesión re-emitida del dueño existente.
      if (res.already_claimed) {
        partnerTag({ origin: 'pre_registro', claim_step: 'resume_payment' });
        if (res.plan_resolved) {
          setPlanAlreadyResolved(true);
          partnerEvent('claim_resume_already_resolved');
        } else if (res.session) {
          persistSession(res.session);
          setClaimedSession(res.session);
          partnerEvent('claim_resume_payment_open');
        } else {
          // Estado inesperado del backend: already_claimed=true pero sin plan_resolved NI session.
          // Por contrato (ver ClaimOpenResult) no deberia pasar, pero sin este guard el codigo caia
          // al formulario normal de OTP/crear-cuenta — que siempre fallaria (la cuenta YA existe) y
          // dejaria al dueno atascado sin explicacion. Defensivo: avisar y mandarlo a iniciar sesion.
          setInvalid('Tu cuenta ya está activa. Inicia sesión normal en el panel de Plick.');
          partnerEvent('claim_resume_unresolved_state');
        }
        return;
      }

      if (res.negocio?.nombre_contacto) setNombre(res.negocio.nombre_contacto);
      if (res.negocio?.correo_contacto) setCorreo(res.negocio.correo_contacto);
      // open() ya envio el OTP: si salio, arranca el cooldown. Si el proveedor fallo
      // (otp_sent=false), deja el reenvio disponible de inmediato.
      if (res.otp_sent) setCooldown(RESEND_SECONDS);
      // Analytics: highest-value funnel. Mark the pre_registro origin, pin the replay and record the
      // open; flag when the OTP could not be delivered (funnel-blocking upstream failure).
      partnerTag({ origin: 'pre_registro', claim_step: 'open' });
      partnerUpgrade('pre_registro');
      partnerEvent('claim_open');
      if (!res.otp_sent) partnerTag({ error_reason: 'otp_send_failed' });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setInvalid(status === 410 ? 'Este enlace ya no es valido o expiro.' : 'No encontramos este enlace.');
      partnerEvent('claim_open_invalid');
      partnerTag({ error_reason: status === 410 ? 'token_expired' : 'not_found' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    open();
  }, [open]);

  // Countdown del cooldown de reenvio (1s por tick mientras quede tiempo).
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = window.setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const onResend = async () => {
    if (!data || cooldown > 0 || resending) return;
    partnerEvent('claim_otp_resend');
    setResending(true);
    setResendNote(null);
    setError(null);
    // Un reenvio implica recapturar: liberamos el input y la verificacion previa, e invalidamos
    // cualquier verifyOtp en vuelo del codigo anterior (ver comentario de verifyRequestRef arriba).
    verifyRequestRef.current += 1;
    setOtp('');
    setIsOtpValid(false);
    setOtpError(null);
    setCooldown(RESEND_SECONDS); // cooldown inmediato (60s), igual que el registro normal
    try {
      const res = await claimResendOtp(token);
      setData((prev) => (prev ? { ...prev, otp_sent: res.otp_sent, phone: res.phone ?? prev.phone } : prev));
      setResendNote(
        res.otp_sent ? 'Te reenviamos el codigo por WhatsApp.' : 'Si no te llega, vuelve a intentar en unos segundos.',
      );
    } catch {
      setResendNote('No pudimos reenviar el codigo. Intenta de nuevo.');
    } finally {
      setResending(false);
    }
  };

  // Verifica el OTP en vivo (modo PEEK: NO quema el código, eso solo lo hace `claimComplete`). Igual
  // que el registro normal, se dispara solo al alcanzar OTP_LENGTH dígitos. 200 → válido + bloquea
  // el input; 400 → mensaje sin romper (deja reintentar borrando y reescribiendo).
  const verifyOtp = async (code: string) => {
    const requestId = (verifyRequestRef.current += 1);
    setVerifyingOtp(true);
    setOtpError(null);
    try {
      await claimVerifyOtp(token, code);
      // Respuesta superada por un reenvio (verifyRequestRef avanzo mientras esta request seguia en
      // vuelo): descartarla — aplicarla pisaria el otp/isOtpValid ya reseteados por onResend.
      if (verifyRequestRef.current !== requestId) return;
      setIsOtpValid(true);
      partnerTag({ claim_step: 'otp_verified' });
      partnerEvent('claim_otp_valid');
    } catch (err: unknown) {
      if (verifyRequestRef.current !== requestId) return;
      const status = (err as { response?: { status?: number } })?.response?.status;
      setIsOtpValid(false);
      setOtpError(
        status === 400
          ? 'El código no es válido. Revísalo e inténtalo de nuevo.'
          : 'No pudimos verificar el código. Intenta de nuevo.',
      );
      if (status === 400) {
        partnerEvent('claim_otp_invalid');
        partnerTag({ error_reason: 'otp_invalid' });
      }
    } finally {
      // A diferencia de isOtpValid/otpError (que solo debe tocar la request vigente), `verifyingOtp`
      // SIEMPRE se libera aqui: si no lo hace, un reenvio a mitad de un verify en vuelo dejaria
      // `verifyingOtp` atorado en `true` para siempre (nadie mas lo resetea), bloqueando permanentemente
      // el guard `!verifyingOtp` de onOtpChange y con eso cualquier verificacion futura.
      setVerifyingOtp(false);
    }
  };

  // Solo dígitos, tope OTP_LENGTH. Al completar la longitud (y si aún no está validado) dispara la
  // verificación en vivo. Una vez validado el input queda bloqueado, así que esto no se reejecuta.
  const onOtpChange = (raw: string) => {
    if (isOtpValid) return;
    const digits = raw.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtp(digits);
    setOtpError(null);
    // verifyOtp maneja sus propios errores (no rechaza); fire-and-forget intencional.
    if (digits.length === OTP_LENGTH && !verifyingOtp) verifyOtp(digits);
  };

  // Limpia el error inline del campo en cuanto el dueño empieza a corregirlo.
  const updateField = (set: (v: string) => void, key: FieldKey) => (v: string) => {
    set(v);
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setError(null);

    // Validación JS de los required: no dependemos de la burbuja nativa del navegador (que también
    // se siente como "no pasa nada"). Marcamos cada campo faltante con su error inline (FieldV4).
    const errs: Partial<Record<FieldKey, string>> = {};
    if (!nombre.trim()) errs.nombre = 'Ingresa tu nombre.';
    if (!correo.trim()) errs.correo = 'Ingresa tu correo.';
    if (!usuario.trim()) errs.usuario = 'Elige un usuario.';
    if (!password) errs.password = 'Crea una contraseña.';
    if (!terms) errs.terms = 'Debes aceptar los términos y condiciones.';
    setFieldErrors(errs);
    if (!isOtpValid) setOtpError('Verifica el código de WhatsApp antes de continuar.');
    if (Object.keys(errs).length > 0 || !isOtpValid) {
      partnerEvent('claim_submit_incomplete');
      return;
    }

    setSubmitting(true);
    try {
      const res = await claimComplete({
        token,
        phone: data.phone || '',
        otp_code: otp.trim(),
        usuario: usuario.trim(),
        password,
        nombre: nombre.trim(),
        correo: correo.trim(),
        acepta_terminos: terms,
      });
      // Persistimos la sesion plena (para autenticar el paso de plan) y mostramos la seleccion de
      // plan. El redirect al panel ocurre al elegir plan o "Continuar con plan gratis".
      persistSession(res.session);
      // Analytics: account activated — the conversion event of the whole PRD-037 funnel. Pin the
      // replay so the successful claim is always retained.
      partnerTag({ claim_step: 'activated', claim_result: 'success' });
      partnerEvent('claim_activated');
      partnerUpgrade('claim_activated');
      setClaimedSession(res.session);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      partnerEvent('claim_activate_error');
      partnerTag({
        claim_result: 'error',
        error_reason:
          status === 400 ? 'otp_invalid' : status === 409 ? 'username_taken' : status === 410 ? 'link_used' : 'unknown',
      });
      if (status === 409) {
        // USERNAME_TAKEN: el usuario elegido ya existe. El backend NO consumió el OTP (lo valida en
        // modo peek y solo lo quema tras crear al usuario), así que el dueño solo cambia el usuario y
        // reenvía con el MISMO código. Marcamos el error inline en el campo `usuario` — NO en el OTP —
        // para no repetir el bug donde una colisión de usuario se mostraba como "código inválido".
        setFieldErrors((prev) => ({ ...prev, usuario: 'Ese usuario ya está en uso. Elige otro.' }));
      } else if (status === 400) setError('El codigo de verificacion es invalido.');
      else if (status === 410) setError('El enlace ya fue utilizado.');
      else setError('No se pudo completar el registro. Intenta de nuevo.');
    } finally {
      // Se resetea SIEMPRE (no solo en el catch): en el camino de exito el form hoy se desmonta
      // (setClaimedSession → render de ClaimPlanStep), pero dejar `submitting=true` deja el boton
      // deshabilitado de forma permanente si ese desmontaje cambiara. `finally` cubre ambos caminos.
      setSubmitting(false);
    }
  };

  // Claim completado → paso de seleccion de plan + pago (o quedarse gratis). onDone redirige al panel.
  if (claimedSession) {
    return <ClaimPlanStep onDone={goToPanel} commerceName={claimedSession.commerce_name} />;
  }

  // Reanudar pago: el pago ya se resolvió por otra vía antes de que el dueño abriera el enlace.
  if (planAlreadyResolved) {
    return (
      <PageShell narrow>
        <Card>
          <Title>Tu plan ya está al día ✅</Title>
          <Muted style={{ marginTop: 8 }}>
            El pago de tu plan ya se completó. Puedes iniciar sesión normal en el panel de Plick.
          </Muted>
          <Button style={{ marginTop: 16 }} onClick={goToPanel}>
            Ir al panel
          </Button>
        </Card>
      </PageShell>
    );
  }

  if (loading) {
    return (
      <PageShell narrow>
        <Card>
          <Title>Cargando...</Title>
        </Card>
      </PageShell>
    );
  }

  if (invalid) {
    return (
      <PageShell narrow>
        <Card>
          <Title>Enlace no disponible</Title>
          <ErrorNote>{invalid}</ErrorNote>
        </Card>
      </PageShell>
    );
  }

  const businessName = data?.negocio?.nombre;
  const masked = maskPhone(data?.phone);
  const testMode = Boolean(data?.otp_test_mode);

  return (
    <PageShell narrow>
      <Card>
        <Title
          sub={
            businessName ? (
              <>
                Tu comercio <strong>{businessName}</strong> ya está casi listo. Solo confirma tu WhatsApp y crea tu
                acceso — te toma menos de 1 minuto.
              </>
            ) : (
              'Tu comercio ya está casi listo. Solo confirma tu WhatsApp y crea tu acceso — te toma menos de 1 minuto.'
            )
          }
        >
          ¡Bienvenido a Plick! 🎉
        </Title>
        {data?.menu_preview?.categorias?.length ? (
          <Muted style={{ color: '#037E00' }}>
            Tu menú ya tiene {data.menu_preview.categorias.length} categoría(s) cargada(s).
          </Muted>
        ) : null}
        {testMode ? (
          <div
            style={{
              margin: '14px 0',
              padding: '10px 12px',
              background: '#FFF8EC',
              border: `1px solid ${C.warning}`,
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.5,
              color: '#8A5A00',
            }}
          >
            🧪 Modo prueba — ingresa el código <strong>0000</strong>.
          </div>
        ) : null}
        <form onSubmit={onSubmit} style={{ marginTop: 12 }}>
          <SectionTitle>Confirma tu WhatsApp</SectionTitle>
          {!isOtpValid ? (
            data?.otp_sent === false ? (
              <Banner tone="warning">
                ⚠️ No pudimos enviar el código a tu WhatsApp{masked ? ` (${masked})` : ''}. Toca{' '}
                <strong>Reenviar código</strong> para volver a intentarlo.
              </Banner>
            ) : (
              <Banner tone="info">
                📲 Te enviamos un código por WhatsApp
                {masked ? (
                  <>
                    {' '}
                    a <strong>{masked}</strong>
                  </>
                ) : (
                  ''
                )}
                . Escríbelo abajo: <strong>se valida solo</strong>, no necesitas tocar ningún botón.
              </Banner>
            )
          ) : null}
          <div style={{ marginBottom: 14 }}>
            <FieldV4 label="Código de verificación:" required error={otpError ?? undefined}>
              <TextInputV4
                value={otp}
                onChange={(v) => onOtpChange(String(v))}
                placeholder="0000"
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                disabled={isOtpValid}
                aria-label="Código de verificación"
              />
            </FieldV4>
            {isOtpValid ? (
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.success, marginTop: 4 }}>
                Código verificado ✓
              </span>
            ) : verifyingOtp ? (
              <Muted style={{ margin: '4px 0 0' }}>Verificando código...</Muted>
            ) : (
              <Muted style={{ margin: '4px 0 0' }}>Escribe los {OTP_LENGTH} dígitos; se valida solo.</Muted>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '-2px 0 14px' }}>
            <span style={{ fontSize: 13, color: C.muted }}>¿No te llegó?</span>
            <Button type="button" variant="ghost" size="sm" onClick={onResend} disabled={cooldown > 0 || resending}>
              {resending
                ? 'Reenviando...'
                : cooldown > 0
                  ? `Reenviar código · disponible en ${cooldown}s`
                  : 'Reenviar código'}
            </Button>
          </div>
          {resendNote ? <Muted style={{ margin: '0 0 12px' }}>{resendNote}</Muted> : null}
          <div style={{ marginTop: 8 }}>
            <SectionTitle>Crea tu acceso</SectionTitle>
          </div>
          <Field
            label="Tu nombre"
            value={nombre}
            onChange={updateField(setNombre, 'nombre')}
            required
            error={fieldErrors.nombre}
          />
          <Field
            label="Correo"
            type="email"
            value={correo}
            onChange={updateField(setCorreo, 'correo')}
            required
            error={fieldErrors.correo}
          />
          <Field
            label="Usuario"
            value={usuario}
            onChange={updateField(setUsuario, 'usuario')}
            autoComplete="username"
            required
            error={fieldErrors.usuario}
          />
          <Field
            label="Contraseña"
            type="password"
            value={password}
            onChange={updateField(setPassword, 'password')}
            autoComplete="new-password"
            required
            error={fieldErrors.password}
          />
          <label
            style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#6B6B6B', marginTop: 4 }}
          >
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => {
                setTerms(e.target.checked);
                setFieldErrors((prev) => (prev.terms ? { ...prev, terms: undefined } : prev));
              }}
            />
            <span>
              Acepto los{' '}
              <a
                href="https://plick.com.mx/terminosycondiciones"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: C.secondary, textDecoration: 'underline' }}
              >
                términos y condiciones
              </a>
            </span>
          </label>
          {fieldErrors.terms ? (
            <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.danger, marginTop: 4 }}>
              {fieldErrors.terms}
            </span>
          ) : null}
          {error && <ErrorNote>{error}</ErrorNote>}
          <Button type="submit" fullWidth disabled={submitting || !isOtpValid} style={{ marginTop: 18 }}>
            {submitting ? 'Activando tu cuenta...' : 'Activar mi cuenta'}
          </Button>
          {!isOtpValid ? (
            <Muted style={{ margin: '8px 0 0', textAlign: 'center' }}>
              Verifica el código de WhatsApp para activar tu cuenta.
            </Muted>
          ) : null}
        </form>
      </Card>
    </PageShell>
  );
};

export default ClaimPage;
